import { API_BASE_URL } from './api';

class ApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface BookingService {
  booking_service_id: number;
  booking_id: number;
  service_id: number;
  quantity: number;
  price: number;
  created_at: string;
  name?: string;
  description?: string;
}

export interface Commission {
  booking_id: number;
  booking_number: number;
  user_id: number;
  vehicle_id: number;
  address_id?: number;
  booking_date: string;
  start_time: string;
  end_time: string;
  payment_date: string;
  status:
    | 'pending'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'rejected'
    | 'assign_washer'
    | 'pickup'
    | 'work_in_progress'
    | 'drop';
  total_amount: number;
  discount_amount: number;
  tax_amount: number;
  final_amount: number;
  payment_status: string;
  payment_method?: string | null;
  notes?: string | null;
  customer_name: string;
  customer_email: string;
  customer_mobile: string;
  registration_number: string;
  car_brand: string;
  car_model: string;
  address_line1?: string;
  address_line2?: string | null;
  city?: string;
  state?: string;
  postal_code?: string;
  created_at?: string;
  updated_at?: string;
  assigned_staff_id?: number | null;
  staff_name?: string | null;
  staff_mobile?: string | null;
  commission_percentage?: number;
  startDate?: string;
  endDate?: string;
}

/**
 * Generic fetch helper with unified error handling
 */
async function apiFetch(url: string, options?: RequestInit) {
  const fullUrl = `${API_BASE_URL}${url}`;
  console.log('📡 Fetching:', fullUrl, options);

  const response = await fetch(fullUrl, {
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    ...options,
  });

  const contentType = response.headers.get('content-type');
  const data =
    contentType && contentType.includes('application/json')
      ? await response.json()
      : await response.text();

  if (!response.ok) {
    console.error('❌ API error', response.status, data);
    throw new ApiError(data?.message || 'API Error', response.status);
  }

  return data;
}

/* -------------------------
   📘 GET: All Bookings
------------------------- */
export const getBookings = async (
  status?: string,
  startDate?: string,
  endDate?: string,
): Promise<Commission[]> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('from_date', startDate);
    if (endDate) params.append('to_date', endDate);

    const data = await apiFetch(`/commissions?${params.toString()}`, {
      method: 'GET',
      cache: 'no-store',
    });

    let bookingsData: any[] = [];

    if (Array.isArray(data)) bookingsData = data;
    else if (data?.data?.commissions) bookingsData = data.data.commissions;
    else if (Array.isArray(data?.data)) bookingsData = data.data;
    else if (Array.isArray(data?.commissions)) bookingsData = data.commissions;
    else {
      console.warn('Unexpected response format:', data);
      return [];
    }

    const transformed = bookingsData.map((b: any) => ({
      booking_id: b.booking_id,
      booking_number:b.booking_number,
      user_id: b.user_id,
      vehicle_id: b.vehicle_id,
      address_id: b.address_id,
      booking_date: b.booking_date,
      start_time: b.start_time,
      end_time: b.end_time,
      status: b.status,
      payment_date: b.payment_date,
      total_amount: parseFloat(b.total_amount) || 0,
      discount_amount: parseFloat(b.discount_amount) || 0,
      tax_amount: parseFloat(b.tax_amount) || 0,
      final_amount: parseFloat(b.final_amount) || 0,
      payment_status: b.payment_status,
      payment_method: b.payment_method,
      notes: b.notes,
      customer_name: `Customer ${b.user_id}`,
      customer_email: '',
      customer_mobile: '',
      registration_number: '',
      car_brand: '',
      car_model: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      created_at: b.created_at,
      updated_at: b.updated_at,
      assigned_staff_id: b.assigned_staff_id,
      staff_name: '',
      staff_mobile: '',
    }));

    return transformed;
  } catch (err: any) {
    console.error('Error fetching bookings:', err);
    throw new ApiError(err.message || 'Failed to fetch bookings');
  }
};


